import React from 'react';
import { imageUrlFromPath, normalizeReportMediaItem } from './carReportUtils';

export function isReportImageFile(item) {
    const normalized = normalizeReportMediaItem(item);
    if (!normalized) return false;
    if (normalized.fileType === 'image') return true;
    if (normalized.fileType === 'file') return false;
    const probe = String(
        normalized.originalname || normalized.filename || normalized.imgPath || ''
    ).toLowerCase();
    const base = probe.split(/[/\\]/).pop();
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test(base);
}

export function reportImageSrc(item) {
    const normalized = normalizeReportMediaItem(item);
    if (!normalized || !isReportImageFile(normalized)) return '';
    return imageUrlFromPath(normalized.imgPath);
}

/** Inline photos for printed CAR / 8D reports. */
export function ReportPhotoGrid({ items, className = '' }) {
    const photos = (items || []).map(normalizeReportMediaItem).filter(isReportImageFile);
    if (!photos.length) return null;

    return (
        <div
            className={className}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '6px 0 10px' }}
        >
            {photos.map((item, idx) => {
                const src = reportImageSrc(item);
                if (!src) return null;
                const label = item.originalname || item.filename || `Photo ${idx + 1}`;
                return (
                    <figure
                        key={`${item.filename || 'photo'}-${idx}`}
                        style={{ margin: 0, maxWidth: '240px', pageBreakInside: 'avoid' }}
                    >
                        <img
                            src={src}
                            alt={label}
                            loading="eager"
                            decoding="sync"
                            style={{ maxWidth: '240px', maxHeight: '180px', objectFit: 'contain', display: 'block' }}
                        />
                        <figcaption style={{ fontSize: '9px', color: '#495057', marginTop: '2px' }}>{label}</figcaption>
                    </figure>
                );
            })}
        </div>
    );
}

/** Thumbnails on the edit form (CAR problem photos, 8D attachments). */
export function ReportPhotoPreviewList({ items, onRemove, readOnly = false }) {
    const photos = (items || []).map(normalizeReportMediaItem).filter(isReportImageFile);
    if (!photos.length) return null;

    return (
        <div className="d-flex flex-wrap gap-3 mt-2 mb-2">
            {photos.map((item, idx) => {
                const src = reportImageSrc(item);
                const label = item.originalname || item.filename || `Photo ${idx + 1}`;
                const sourceIndex = (items || []).indexOf(item);
                return (
                    <div key={`${item.filename}-${idx}`} style={{ maxWidth: '140px' }}>
                        {src ? (
                            <a href={src} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={src}
                                    alt={label}
                                    style={{
                                        width: '132px',
                                        height: '99px',
                                        objectFit: 'contain',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        background: '#f8f9fa',
                                    }}
                                />
                            </a>
                        ) : null}
                        <div className="small text-truncate" title={label}>{label}</div>
                        {!readOnly && onRemove && sourceIndex >= 0 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0"
                                onClick={() => onRemove(sourceIndex)}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function ReportNonImageFileList({ items }) {
    const files = (items || []).filter((item) => item && !isReportImageFile(item));
    if (!files.length) return null;

    return (
        <ul style={{ margin: '6px 0', paddingLeft: '18px', fontSize: '10px' }}>
            {files.map((f, i) => {
                const label = f.originalname || f.filename || 'Attachment';
                const href = f.imgPath ? imageUrlFromPath(f.imgPath) : '';
                return (
                    <li key={`file-${i}`}>
                        {href ? (
                            <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
                        ) : (
                            label
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
